"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import "./HomeLanding.scss";
import {
  blogCategorySlugFromName,
  extractCategoryNameFromCategoriesField,
} from "../blog/_lib/categorySlug";

// three.js は SSR と相性が悪いためクライアント限定で読み込む
const ServiceCard3D = dynamic<{ type: "web" | "seo" | "ads" | "meo" }>(
  () => import("@/app/__components/ServiceCard3D"),
  { ssr: false },
);

type Content = {
  id: string;
  title: string;
  category?:
    | {
        id: string;
        name: string;
      }
    | string;
  categories?: unknown;
  eyecatch?: {
    url: string;
  };
  thumbnail?: {
    url: string;
  };
  body?: string;
  publishedAt?: string;
};

type Props = {
  blogs: Content[];
  news: Content[];
  works: Content[];
};

function formatDateYMD(dateString?: string): string {
  if (!dateString) return "";
  const iso = dateString.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [year, month, day] = iso.split("-");
  return `${year}.${month}.${day}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pickString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

// works用: microCMSで設定したカテゴリ名をそのまま表示（idにはフォールバックしない）
function getWorksCategoryName(value: unknown): string {
  if (!value) return "";

  if (typeof value === "string") return value;

  // 配列（複数カテゴリなど）
  if (Array.isArray(value)) {
    const first = value[0] as unknown;
    return first ? getWorksCategoryName(first) : "";
  }

  if (!isRecord(value)) return "";

  // 参照先が「name」ではなく「title」等のケースも吸収
  const directCategoryText = pickString(value.category);
  const nestedCategory = isRecord(value.category) ? value.category : undefined;

  return (
    directCategoryText ||
    pickString(value.name) ||
    pickString(value.title) ||
    pickString(nestedCategory?.name) ||
    pickString(nestedCategory?.title) ||
    pickString(value.label) ||
    pickString(value.value) ||
    ""
  );
}

function getWorksCategoryLabel(work: unknown): string {
  if (!isRecord(work)) return "";

  // まずはありがちなフィールド名で拾う
  const direct =
    getWorksCategoryName(work?.category) ||
    getWorksCategoryName(work?.categories) ||
    getWorksCategoryName(work?.workCategory) ||
    getWorksCategoryName(work?.worksCategory) ||
    getWorksCategoryName(work?.categoryRef) ||
    getWorksCategoryName(work?.categoryRefs);
  if (direct) return direct;

  // 次に、キー名に category/categories を含むものを探索（tag/thumbnail 等は除外）
  const excludedKeys = new Set([
    "tag",
    "tags",
    "thumbnail",
    "eyecatch",
    "image",
  ]);
  for (const [key, value] of Object.entries(work)) {
    if (excludedKeys.has(key)) continue;
    if (key === "categoryKey") continue;
    if (!/categor/i.test(key)) continue; // category/categories 等
    const found = getWorksCategoryName(value);
    if (found) return found;
  }

  return "";
}

export default function HomeLanding({ blogs, news, works }: Props) {
  const blogCards = blogs.slice(0, 3);
  const worksCards = works.slice(0, 3);
  const [enable3d, setEnable3d] = useState(false);
  const [show3d, setShow3d] = useState(false);
  const serviceDeckRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
    setEnable3d(!(reduceMotion || coarsePointer));
  }, []);

  useEffect(() => {
    if (!enable3d) return;
    const target = serviceDeckRef.current;
    if (!target) return;

    if (!("IntersectionObserver" in window)) {
      setShow3d(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShow3d(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [enable3d]);

  return (
    <>
      <section id="top" className="fv-section">
        <div className="section-header">
          <h1 className="main-title fade-up">
            <span className="main-title-eyebrow stroke">
              Marketing & Tech for Challengers.
            </span>
            <br />
            「良いモノなのに売れない」
            <br />
            をなくす
          </h1>
        </div>
        <div className="service-deck" ref={serviceDeckRef}>
          {[
            {
              label: "01",
              title: "サイト改善",
              descDefault: "高速なサイト表示と高いCVR設計で成果を伸ばす。",
              descHover:
                "「商談・受注」を目的に、ヘッドレスCMSとJamstackアーキテクチャによる高速表示で成果を最大化。",
              link: "/service/web-improvement",
            },
            {
              label: "02",
              title: "SEO/GEO記事制作",
              descDefault:
                "AI×自社RAGで、SEOとAI検索の両方に効く記事を継続制作。",
              descHover:
                "貴社の実績・ノウハウをAIに参照させる記事制作代行。商談につながる記事を継続的に。",
              link: "/service/seo",
            },
            {
              label: "03",
              title: "広告運用",
              descDefault: "Google/Meta特化×認定パートナー",
              descHover:
                "ユーザーインタビューからターゲットの解像度を高めた、クリエイティブ制作で効果を。",
              link: "/service/ad",
            },
          ].map(
            ({ label, title, descDefault, descHover, link }) => {
              const type =
                label === "01" ? "web" : label === "02" ? "seo" : "ads";
              return (
                <Link
                  key={label}
                  href={link}
                  className="service-card hover-target fade-up"
                >
                  <div className="card-3d-container">
                    {enable3d && show3d ? <ServiceCard3D type={type} /> : null}
                  </div>
                  <div className="card-number-container">
                    <span className="card-number">{label}</span>
                    <h2 className="card-title">{title}</h2>
                  </div>
                  <div className="card-content">
                    <p className="card-desc card-desc-default">{descDefault}</p>
                    <p className="card-desc card-desc-hover">{descHover}</p>
                  </div>
                </Link>
              );
            },
          )}
        </div>
        <div className="scroll-indicator" style={{ opacity: 1 }}>
          <span className="scroll-text">SCROLL</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      <section>
        <div className="section-header fade-up">
          <span className="section-label">CASE STUDY</span>
          <h2 className="h2-title">実績紹介</h2>
        </div>
        <div className="article-grid">
          {worksCards.length ? (
            worksCards.map((work, index) => (
              <Link
                key={work.id}
                href={`/works/${work.id}`}
                className="article-card hover-target fade-up"
              >
                <div
                  className="article-thumb"
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1000 / 600",
                    height: "auto",
                    overflow: "hidden",
                    background: "#111",
                  }}
                >
                  {work.thumbnail?.url ? (
                    <Image
                      src={work.thumbnail.url}
                      alt={work.title || "Works thumbnail"}
                      fill
                      style={{ objectFit: "contain" }}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(135deg, #1f1f1f, #2f2f2f)",
                      }}
                    />
                  )}
                  <div className="works-result">
                    {(() => {
                      const categoryName = getWorksCategoryLabel(work);
                      return categoryName || `WORK ${index + 1}`;
                    })()}
                  </div>
                </div>
                <div className="article-body">
                  <div className="article-meta">
                    <span className="article-tag">
                      {work.publishedAt
                        ? formatDateYMD(work.publishedAt)
                        : "最新実績"}
                    </span>
                  </div>
                  <h3
                    className="article-title"
                    dangerouslySetInnerHTML={{ __html: work.title ?? "" }}
                  />
                </div>
              </Link>
            ))
          ) : (
            <p className="fade-up text-muted">支援実績を準備中です。</p>
          )}
        </div>
        <div className="fade-up">
          <Link href="/works" className="btn-arrow hover-target">
            View All Works →
          </Link>
        </div>
      </section>

      <section>
        <div className="meo-container">
          <div className="fade-up">
            <span className="section-label">MEO DASHBOARD</span>
            <h2 className="h2-title spacing">
              効果が見える。
              <br />
              だから、売上が伸びる。
            </h2>
            <p className="copy">
              ブラックボックスになりがちなMEO対策を可視化。
              順位推移、インサイト、口コミ管理を一つのダッシュボードで。
              正しい施策を打つための羅針盤です。
            </p>
            <Link href="/service/meo-tool" className="btn-arrow hover-target">
              Learn More →
            </Link>
          </div>
          <div className="dashboard-ui fade-up">
            <Image
              src="/images/top-meo.png"
              alt="MEO dashboard"
              fill
              style={{ objectFit: "cover" }}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              priority={false}
            />
          </div>
        </div>
      </section>

      <section>
        <div className="section-header fade-up">
          <span className="section-label">KNOWLEDGE</span>
          <h2 className="h2-title">お役立ちブログ</h2>
        </div>
        <div className="article-grid">
          {blogCards.length ? (
            blogCards.map((post) => (
              <Link
                key={post.id}
                href={`/blog/${encodeURIComponent(
                  blogCategorySlugFromName(
                    extractCategoryNameFromCategoriesField(post.categories),
                  ),
                )}/${post.id}`}
                className="article-card hover-target fade-up"
              >
                <div
                  className="article-thumb"
                  style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1000 / 600",
                    overflow: "hidden",
                    background: "#111",
                  }}
                >
                  {post.eyecatch?.url ? (
                    <Image
                      src={post.eyecatch.url}
                      alt={post.title || "Blog thumbnail"}
                      fill
                      style={{ objectFit: "contain" }}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(135deg, #1f1f1f, #2f2f2f)",
                      }}
                    />
                  )}
                </div>
                <div className="article-body">
                  <div className="article-meta">
                    <span>
                      {post.publishedAt
                        ? formatDateYMD(post.publishedAt)
                        : "最新記事"}
                    </span>
                    {extractCategoryNameFromCategoriesField(
                      post.categories,
                    ) && (
                      <span className="article-tag">
                        {extractCategoryNameFromCategoriesField(
                          post.categories,
                        )}
                      </span>
                    )}
                  </div>
                  <h3 className="article-title">{post.title}</h3>
                </div>
              </Link>
            ))
          ) : (
            <p className="fade-up text-muted">ブログ記事を準備中です。</p>
          )}
        </div>
        <div className="fade-up">
          <Link href="/blog" className="btn-arrow hover-target">
            View All Articles →
          </Link>
        </div>
      </section>

      {news.length > 0 && (
        <section>
          <div className="section-header fade-up">
            <span className="section-label">NEWS</span>
            <h2 className="h2-title">ニュース</h2>
          </div>
          <div className="news-list fade-up">
            <ul>
              {news.slice(0, 4).map((item) => (
                <li key={item.id}>
                  <span>
                    {item.publishedAt ? formatDateYMD(item.publishedAt) : ""}
                  </span>
                  <Link href={`/news/${item.id}`} className="hover-target">
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="fade-up">
            <Link href="/news" className="btn-arrow hover-target">
              View All News →
            </Link>
          </div>
        </section>
      )}

      <section>
        <div className="recruit-content fade-up">
          <span className="section-label">JOIN US</span>
          <h2 className="h2-title">採用情報</h2>
          <p className="recruit-msg">
            あなたの力を貸してください。
            <br />
            <strong>社内の売上ではなく、お客様の売上を追いたい</strong>
            <br />
            そんな仲間を探しています。
          </p>
          <Link href="/recruit" className="btn-side hover-target">
            RECRUIT SITE →
          </Link>
        </div>
      </section>

      <section className="cta-section">
        <div className="fade-up">
          <p className="section-label">START YOUR PROJECT</p>
          <h2 className="h2-title">
            ビジネスを加速させる
            <br />
            最初のステップ。
          </h2>
          <p className="copy">
            どうすればWEB経由の問い合わせが増えるか、一緒に作戦会議しませんか？
            お気軽にお問い合わせください。
          </p>
          <Link href="/contact" className="cta-btn-large hover-target">
            無料相談を予約する
          </Link>
        </div>
      </section>
    </>
  );
}
